import React, { useState } from "react";
import {
    Container, Typography, Tabs, Tab, Box,
    Table, TableHead, TableRow, TableCell, TableBody,
    Accordion, AccordionSummary, AccordionDetails, RadioGroup,
    FormControlLabel, Radio, CircularProgress, Grid, Paper, Divider, Skeleton
} from "@mui/material";
import CustomDialog from "../../../components/layouts/common/CustomDialog";

const TabPanel = ({ children, value, index }) => (
    <Box hidden={value !== index} sx={{ pt: 2 }}>
        {value === index && children}
    </Box>
);

const ProductUi = ({ data, products, loading, productId, setProductId }) => {
    const { name, item = [], problems = [], product_accessories = [] } = data;
    const [tab, setTab] = useState(0);
    const partsCol = 3;
    const problemsCol = 2;
    const accessoriesCol = 1;

    return (
        <CustomDialog
            loading={loading}
            closeText="Close"
            id={"product"}
        >
            <Container maxWidth="xl" sx={{ py: 2 }}>
                <Paper elevation={4} sx={{ p: 6, borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
                        Product View
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                                <Accordion expanded square disableGutters elevation={0}>
                                    <AccordionSummary sx={{ cursor: "default", pb: 1 }}>
                                        <Typography variant="h6" fontWeight={600}>
                                            {productId ? products.find(p => p._id === productId)?.name : "Select Product"}
                                        </Typography>
                                    </AccordionSummary>
                                    <Divider />
                                    <AccordionDetails sx={{ pt: 1 }}>
                                        {loading ? (
                                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                                <CircularProgress size={24} />
                                            </Box>
                                        ) : (
                                            <RadioGroup value={productId} onChange={(e) => setProductId(e.target.value)}>
                                                {products.map(product => (
                                                    <FormControlLabel
                                                        key={product._id}
                                                        value={product._id}
                                                        control={<Radio />}
                                                        label={product.name}
                                                        sx={{ mb: 0.5 }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={9}>
                            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="h3" fontWeight={700} gutterBottom>
                                    {name ? `${name} — Data Overview` : "Product Details"}
                                </Typography>

                                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                                    <Tab label="Parts" sx={{ textTransform: "none", fontSize: "1.2rem", fontWeight: 600 }} />
                                    <Tab label="Problems" sx={{ textTransform: "none", fontSize: "1.2rem", fontWeight: 600 }} />
                                    <Tab label="Accessories" sx={{ textTransform: "none", fontSize: "1.2rem", fontWeight: 600 }} />
                                </Tabs>

                                <TabPanel value={tab} index={0}>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: "silver" }}>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: "20px", fontWeight: 600, color: "#f2f2f2" }}>Part Name</TableCell>
                                                <TableCell sx={{ fontSize: "20px", fontWeight: 600, color: "#f2f2f2" }}>Solution</TableCell>
                                                <TableCell sx={{ fontSize: "20px", fontWeight: 600, color: "#f2f2f2" }}>Price</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loading &&
                                                [0, 1, 2, 3].map((row) => (
                                                    <TableRow key={row}>
                                                        <TableCell colSpan={partsCol}>
                                                            <Skeleton animation="pulse" width="100%" height="50px" />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}

                                            {!loading && item.length === 0 && (
                                                <TableRow sx={{ height: "225px" }}>
                                                    <TableCell colSpan={partsCol}>
                                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                            <Typography variant="h5">No Data Found</Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {!loading && item.map((i, index) => (
                                                <TableRow key={index} hover sx={{ "&:nth-of-type(even)": { backgroundColor: "#f2f2f2" } }}>
                                                    <TableCell sx={{ fontSize: "20px" }}>{i.name}</TableCell>
                                                    <TableCell sx={{ fontSize: "20px" }}>{i.solution}</TableCell>
                                                    <TableCell sx={{ fontSize: "20px" }}>{i.price}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TabPanel>

                                <TabPanel value={tab} index={1}>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: "silver" }}>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: "20px", fontWeight: 600, color: "#f2f2f2" }}>Problem</TableCell>
                                                <TableCell sx={{ fontSize: "20px", fontWeight: 600, color: "#f2f2f2" }}>Warranty</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loading &&
                                                [0, 1, 2, 3].map((row) => (
                                                    <TableRow key={row}>
                                                        <TableCell colSpan={problemsCol}>
                                                            <Skeleton animation="pulse" width="100%" height="50px" />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}

                                            {!loading && problems.length === 0 && (
                                                <TableRow sx={{ height: "225px" }}>
                                                    <TableCell colSpan={problemsCol}>
                                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                            <Typography variant="h5">No Data Found</Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {!loading && problems.map((p, index) => (
                                                <TableRow key={index} hover sx={{ "&:nth-of-type(even)": { backgroundColor: "#f2f2f2" } }}>
                                                    <TableCell sx={{ fontSize: "20px" }}>{p.name}</TableCell>
                                                    <TableCell sx={{ fontSize: "20px" }}>
                                                        {p.warrenty}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TabPanel>

                                <TabPanel value={tab} index={2}>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: "silver" }}>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: "20px", fontWeight: 600, color: "#f2f2f2" }}>Accessory</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loading &&
                                                [0, 1, 2, 3].map((row) => (
                                                    <TableRow key={row}>
                                                        <TableCell colSpan={accessoriesCol}>
                                                            <Skeleton animation="pulse" width="100%" height="50px" />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}

                                            {!loading && product_accessories.length === 0 && (
                                                <TableRow sx={{ height: "225px" }}>
                                                    <TableCell colSpan={accessoriesCol}>
                                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                            <Typography variant="h5">No Data Found</Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {!loading && product_accessories.map((a, index) => (
                                                <TableRow key={index} hover sx={{ "&:nth-of-type(even)": { backgroundColor: "#f2f2f2" } }}>
                                                    <TableCell sx={{ fontSize: "20px" }}>{a.name}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TabPanel>
                            </Paper>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>
        </CustomDialog>
    );
};

export default ProductUi;
